import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from "@angular/core";
import {
	formatTooltipTime,
	getLineChartConfig,
	supportsIntraday,
	TargetPriceFormatter,
	unixTimeToLocaleString
} from "../../utils/chart-utils";
import * as Highcharts from "highcharts/highstock";
import { FiatPrecisionSymbolPipe } from "@modules/core/pipes";
import { Index } from "@models/asset/index/index.model";
import { ChartLineColor, LineChartConfig } from "@modules/shared/chart/models/chart-config.model";
import moment from "moment";
import { PortfolioFacade } from "@modules/portfolio/domain/portfolio.facade";
import { take } from "rxjs/operators";
import { TranslationService } from "@core-services/translation.service";

@Component({
	selector: "line-chart",
	templateUrl: "./line-chart.component.html",
	styleUrls: ["./line-chart.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class LineChartComponent {
	@Input() public set chartConfig(config: LineChartConfig) {
		if (config) {
			this.initChartOptions(config);
			this.updateSeries(config);
		}
	}

	@Input() public isPortfolioPerformance: boolean = false;

	public isChartUpdated: boolean = false;
	public Highcharts: typeof Highcharts = Highcharts;
	public chartOptions: Highcharts.Options | null = null;
	private formattedNumber: string = "";

	public constructor(
		protected changeDetectorRef: ChangeDetectorRef,
		protected fiatPrecisionSymbolPipe: FiatPrecisionSymbolPipe,
		private readonly portfolioFacade: PortfolioFacade,
		protected translationService: TranslationService
	) {}

	private initChartOptions(config: LineChartConfig): void {
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		const that = this;

		if (config.liquidationPrice != null) {
			this.formattedNumber = this.fiatPrecisionSymbolPipe.transform(config.liquidationPrice.value, {
				id: config.fiat.getId(),
				currencies: [config.fiat],
				symbol: true,
				customPrecision: config.liquidationPrice.precision
			});
		}

		const orderTargetPriceFormatter: TargetPriceFormatter = (order) => {
			const fiat = order?.fiat ?? config.fiat;

			const fiatId = config.fiat.getId();

			const value = order?.targetPrice?.value;
			const precision = order?.targetPrice?.precision;

			if (value == null || fiat == null || fiatId == null || precision == null) {
				return "";
			}

			return this.fiatPrecisionSymbolPipe.transform(value, {
				id: fiatId,
				currencies: [config.fiat],
				symbol: true,
				customPrecision: precision
			});
		};

		const formattedText: TextLabels = {
			liquidation: this.translationService.translate("position_detail_liquidation_price", [this.formattedNumber]),
			loss: this.translationService.translate("margin_limit_order_sell_stop_loss") + " ≈ ",
			profit: this.translationService.translate("margin_limit_order_sell_take_profit") + " ≈ "
		};

		this.chartOptions = getLineChartConfig(
			config.language,
			Number(config.liquidationPrice?.value),
			formattedText,
			config.limitOrders,
			orderTargetPriceFormatter
		);

		this.chartOptions.xAxis[0].labels.formatter = (args) =>
			unixTimeToLocaleString(config.language, +args.value, config.timespan);

		this.chartOptions.yAxis[0].labels.formatter = (args) =>
			this.fiatPrecisionSymbolPipe.transform(args.value, {
				largeNumber: true,
				round: false,
				currencies: [config.tradable]
			});

		this.portfolioFacade.isPortfolioDataHidden$.pipe(take(1)).subscribe((isHideValueActive) => {
			let priceBlur = "";
			if (this.isPortfolioPerformance) {
				if (isHideValueActive) {
					this.chartOptions.yAxis[0].labels.style = { filter: "blur(4px)" };
					priceBlur = 'style="filter: blur(4px)"';
				} else {
					this.chartOptions.yAxis[0].labels.style = {
						filter: "none",
						color: "#b4b4b4",
						fontSize: "11px"
					};
					priceBlur = "";
				}
			}

			// Custom tooltip
			this.chartOptions.tooltip.formatter = function () {
				const time = formatTooltipTime(config.language, +this.x, config.timespan);
				const price = that.fiatPrecisionSymbolPipe.transform(this.y, {
					id: config.fiat.getId(),
					currencies: [config.fiat, config.tradable],
					symbol: true,
					removeCurrency: config.tradable instanceof Index
				});

				return (
					'<div class="chart-tooltip-container"><div class="chart-tooltip"><div class="date">' +
					time +
					`</div><div class="price" ${priceBlur}>` +
					price +
					"</div></div></div>"
				);
			};
		});

		let defaultLineWidth: number = 2;
		let defaultColor: string = ChartLineColor.Green;

		if (config.isSmall) {
			defaultLineWidth = 1;
			this.chartOptions.chart.marginTop = 0;
			this.chartOptions.chart.spacing = [0, 0, 0, 0];
			this.chartOptions.tooltip.enabled = false;
			this.chartOptions.xAxis[0].visible = false;
			this.chartOptions.xAxis[0].crosshair = undefined;
			this.chartOptions.yAxis[0].visible = false;
			this.chartOptions.yAxis[0].startOnTick = false;
			this.chartOptions.yAxis[0].endOnTick = false;
			this.chartOptions.yAxis[0].tickPixelInterval = 5; // Scale the axis to fit smaller size
			this.chartOptions.plotOptions.series.states.hover = { enabled: false };
		}

		this.chartOptions.plotOptions.series.lineWidth = config.lineWidth || defaultLineWidth;
		this.chartOptions.plotOptions.series.color = config.lineColor || defaultColor;
	}

	private updateSeries(config: LineChartConfig): void {
		// Reset axis settings before rendering new data set
		this.resetXAxis();

		if (supportsIntraday(config)) {
			this.handleIntraday(config);
		}

		this.chartOptions.series = [
			{
				data: config.data,
				type: "line"
			}
		];

		// Refresh the view
		this.isChartUpdated = true;
		this.changeDetectorRef.markForCheck();
	}

	private resetXAxis(): void {
		this.chartOptions.xAxis[0].min = undefined;
		this.chartOptions.xAxis[0].max = undefined;
		this.chartOptions.xAxis[0].ordinal = true;
		this.chartOptions.xAxis[0].tickPositions = undefined;
	}

	private handleIntraday(config: LineChartConfig): void {
		// Set min and max with offset since BE sends market times in different timezone as candles
		const min = moment(config.lastTradingDayStart).utcOffset(1);
		const max = moment(config.lastTradingDayEnd).utcOffset(1);

		// Set x-axis limits from opening to closing time
		this.chartOptions.xAxis[0].min = min.valueOf();
		this.chartOptions.xAxis[0].max = max.valueOf();
		this.chartOptions.xAxis[0].ordinal = false; // No need for ordinal on intraday + it won't work with min/max

		// Latest decision is to render only 3 labels: 8:00, 15:00, 22:00
		// Set them in default (candle) timezone and Highcharts will adapt for local
		this.chartOptions.xAxis[0].tickPositions = [
			moment(min).set({ hour: 8, minute: 0, second: 0, millisecond: 0 }),
			moment(min).set({ hour: 15, minute: 0, second: 0, millisecond: 0 }),
			moment(min).set({ hour: 22, minute: 0, second: 0, millisecond: 0 })
		];
	}
}

export interface TextLabels {
	liquidation: string;
	loss?: string;
	profit?: string;
}
